import React, { useState } from 'react';
import {
    X,
    Sparkles,
    Download,
    Copy,
    Check,
    ExternalLink,
    Clock,
    ImageIcon
} from 'lucide-react';

export interface ImagePreviewRecord {
    id?: string;
    timestamp: Date;
    endpoint: string;
    success?: boolean;
    error?: string;
    file_name?: string;
    original_url?: string;
    result_url?: string;
    cost?: number;
    duration_ms?: number;
}

interface ImagePreviewModalProps {
    record: ImagePreviewRecord;
    onClose: () => void;
}

export function ImagePreviewModal({ record, onClose }: ImagePreviewModalProps) {
    const hasOriginal = Boolean(record.original_url);
    const hasResult = Boolean(record.result_url);
    const hasBoth = hasOriginal && hasResult;

    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const handleCopy = (url: string, key: string) => {
        navigator.clipboard.writeText(url);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const resp = await fetch(url);
            const blob = await resp.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, '_blank');
        }
    };

    const formatLatency = (ms?: number) => {
        if (ms === undefined) return null;
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
            <div className="relative w-full max-w-5xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="px-5 py-4 border-b border-border bg-card flex items-center justify-between gap-3 flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-primary/10 rounded-xl text-primary flex-shrink-0">
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-foreground text-sm sm:text-base flex items-center gap-2 truncate">
                                <span className="truncate">{record.file_name || 'API Image Request'}</span>
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-mono font-medium flex-shrink-0">
                                    {record.endpoint}
                                </span>
                            </h3>
                            <p className="text-xs text-muted-foreground truncate">
                                {record.timestamp.toLocaleString()} {record.id ? `• ID: ${record.id.slice(0, 8)}` : ''}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-xl transition-colors"
                        title="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Main Body - Side-by-Side View */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-muted/20 flex flex-col justify-center">
                    <div className={`w-full ${hasBoth ? 'grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto' : 'max-w-md mx-auto'}`}>
                        {/* 1. Original Input Card */}
                        {hasOriginal && (
                            <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm flex flex-col">
                                <div className="px-4 py-3 bg-muted/60 border-b border-border flex items-center justify-between">
                                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4 text-primary" /> Original Input Image
                                    </span>
                                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-background border border-border font-mono text-muted-foreground">
                                        Input
                                    </span>
                                </div>

                                {/* Image Box - perfectly fitted with object-contain */}
                                <div className="h-[280px] sm:h-[340px] p-4 bg-background/80 flex items-center justify-center overflow-hidden">
                                    <img
                                        src={record.original_url}
                                        alt="Original Input"
                                        className="w-full h-full object-contain rounded-lg transition-transform duration-200 hover:scale-[1.02]"
                                    />
                                </div>

                                <div className="p-3.5 bg-card border-t border-border flex items-center gap-2.5">
                                    <button
                                        onClick={() => handleCopy(record.original_url!, 'card-orig')}
                                        className="flex-1 py-2 px-3 bg-muted hover:bg-muted/80 text-foreground rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 border border-border transition-all"
                                    >
                                        {copiedKey === 'card-orig' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                        {copiedKey === 'card-orig' ? 'Copied Input URL' : 'Copy Input URL'}
                                    </button>
                                    <button
                                        onClick={() => handleDownload(record.original_url!, `original-${record.file_name || 'image.jpg'}`)}
                                        className="py-2 px-3.5 bg-muted hover:bg-muted/80 text-foreground rounded-xl text-xs font-semibold flex items-center gap-1.5 border border-border transition-all"
                                        title="Download Original Input"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span className="hidden sm:inline">Download</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* 2. Processed Output Card (Transparent PNG) */}
                        {hasResult && (
                            <div className="bg-card border border-green-500/30 rounded-2xl overflow-hidden shadow-sm flex flex-col">
                                <div className="px-4 py-3 bg-green-500/10 border-b border-green-500/20 flex items-center justify-between">
                                    <span className="text-xs font-bold uppercase tracking-wider text-green-600 dark:text-green-400 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4" /> Output (No Background)
                                    </span>
                                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-green-500/20 text-green-600 dark:text-green-400 font-mono font-medium">
                                        Transparent PNG
                                    </span>
                                </div>

                                {/* Image Box with checkerboard canvas for transparent PNG */}
                                <div className="h-[280px] sm:h-[340px] p-4 bg-[linear-gradient(45deg,#222_25%,transparent_25%),linear-gradient(-45deg,#222_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#222_75%),linear-gradient(-45deg,transparent_75%,#222_75%)] bg-[size:16px_16px] bg-[#141414] flex items-center justify-center overflow-hidden">
                                    <img
                                        src={record.result_url}
                                        alt="Output Result"
                                        className="w-full h-full object-contain rounded-lg transition-transform duration-200 hover:scale-[1.02]"
                                    />
                                </div>

                                <div className="p-3.5 bg-card border-t border-border flex items-center gap-2.5">
                                    <button
                                        onClick={() => handleCopy(record.result_url!, 'card-res')}
                                        className="flex-1 py-2 px-3 bg-muted hover:bg-muted/80 text-foreground rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 border border-border transition-all"
                                    >
                                        {copiedKey === 'card-res' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                        {copiedKey === 'card-res' ? 'Copied Output URL' : 'Copy Output URL'}
                                    </button>
                                    <button
                                        onClick={() => handleDownload(record.result_url!, `nobg-${record.file_name || 'result.png'}`)}
                                        className="py-2 px-4 bg-primary text-primary-foreground hover:opacity-90 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-opacity shadow-md shadow-primary/20"
                                        title="Download Background Removed PNG"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span>Download PNG</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-border bg-card flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground flex-shrink-0">
                    <div className="flex items-center gap-4 flex-wrap">
                        {record.duration_ms !== undefined && (
                            <span className="flex items-center gap-1 font-mono">
                                <Clock className="w-3.5 h-3.5 text-primary" /> Latency: {formatLatency(record.duration_ms)}
                            </span>
                        )}
                        {record.cost !== undefined && (
                            <span className="font-semibold text-foreground">
                                Cost: ${record.cost.toFixed(4)}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                        {record.original_url && (
                            <a
                                href={record.original_url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground rounded-lg transition-colors flex items-center gap-1 font-medium"
                            >
                                <ExternalLink className="w-3.5 h-3.5" /> Open Input URL
                            </a>
                        )}
                        {record.result_url && (
                            <a
                                href={record.result_url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors flex items-center gap-1 font-medium"
                            >
                                <ExternalLink className="w-3.5 h-3.5" /> Open Output URL
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
